import { ArrowRight, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'
import type { GtmDemoErrorKey } from '@/features/gtm/api/gtm-demo-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GtmSpinner } from '@/features/gtm/components/gtm-spinner'

export const GTM_URL_INPUT_ID = 'gtm-site-url'
const GTM_URL_ERROR_ID = 'gtm-site-url-error'
const GTM_URL_HINT_ID = 'gtm-site-url-hint'

export function focusGtmUrlInput() {
  const input = document.getElementById(GTM_URL_INPUT_ID)
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches
  input?.scrollIntoView({
    behavior: reducedMotion ? 'auto' : 'smooth',
    block: 'center',
  })
  input?.focus({ preventScroll: true })
}

type GtmUrlFormProps = {
  url: string
  onUrlChange: (url: string) => void
  onSubmit: () => void
  isBusy: boolean
  errorKey: GtmDemoErrorKey | null
}

export function GtmUrlForm({
  url,
  onUrlChange,
  onSubmit,
  isBusy,
  errorKey,
}: GtmUrlFormProps) {
  const { t } = useTranslation('gtm')
  const inputRef = useRef<HTMLInputElement>(null)
  const previousError = useRef<GtmDemoErrorKey | null>(null)
  const isSiteError =
    errorKey === 'invalid_url' ||
    errorKey === 'site_unreachable' ||
    errorKey === 'site_blocked' ||
    errorKey === 'site_empty'

  useEffect(() => {
    if (isSiteError && !previousError.current) inputRef.current?.focus()
    previousError.current = errorKey
  }, [errorKey, isSiteError])

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        // The button stays bright when the field is empty; pressing it then
        // only puts the cursor in the field.
        if (!url.trim()) {
          inputRef.current?.focus()
          return
        }
        onSubmit()
      }}
    >
      <label htmlFor={GTM_URL_INPUT_ID} className="sr-only">
        {t('urlLabel')}
      </label>
      <div className="flex flex-col gap-2 rounded-2xl border border-sidebar-border bg-card/85 p-2 transition-[border-color,box-shadow] duration-200 focus-within:border-primary focus-within:ring-1 focus-within:ring-ring has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-destructive motion-reduce:transition-none sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Globe
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={inputRef}
            id={GTM_URL_INPUT_ID}
            type="text"
            inputMode="url"
            autoComplete="url"
            autoCapitalize="none"
            spellCheck={false}
            value={url}
            onChange={(event) => onUrlChange(event.target.value)}
            aria-invalid={isSiteError ? true : undefined}
            aria-describedby={
              isSiteError
                ? `${GTM_URL_ERROR_ID} ${GTM_URL_HINT_ID}`
                : GTM_URL_HINT_ID
            }
            placeholder={t('urlPlaceholder')}
            className="h-12 rounded-xl border-transparent bg-transparent pl-11 text-base shadow-none focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent md:text-base aria-invalid:border-transparent aria-invalid:ring-0"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isBusy}
          className="h-12 rounded-xl bg-upgrade-cta px-6 text-upgrade-cta-foreground hover:bg-upgrade-cta/90"
        >
          {isBusy ? (
            <>
              <GtmSpinner className="size-4" />
              {t('submitting')}
            </>
          ) : (
            <>
              {t('submit')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
      {errorKey ? (
        <p
          id={GTM_URL_ERROR_ID}
          role="alert"
          className="px-2 text-sm text-destructive"
        >
          {t(`error_${errorKey}`)}
        </p>
      ) : null}
      <p id={GTM_URL_HINT_ID} className="px-2 text-sm text-muted-foreground">
        {t('submitHint')}
      </p>
    </form>
  )
}
